import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Flame, RefreshCw, Filter, MapPin } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { errorMessage } from "@/lib/errors";
import MapView from "@/components/chauffeur/MapView";
import { CircleMarker, Tooltip } from "react-leaflet";

const KIND_FILTERS = [
  { key: "pickup",  label: "Pickups" },
  { key: "dropoff", label: "Drop-offs" },
  { key: "all",     label: "Both" },
];
const DAY_FILTERS = [7, 14, 30, 90];

// Linear-scale circle radius (px) by hit count vs the max in the dataset.
const radiusFor = (count, max) => {
  if (!max || max <= 0) return 8;
  const ratio = Math.min(1, count / max);
  return 8 + Math.round(ratio * 24); // 8 → 32 px
};

export default function AdminHeatmapTab() {
  const [kind, setKind] = useState("pickup");
  const [days, setDays] = useState(30);
  const [points, setPoints] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/locations/heatmap", { params: { kind, days, limit: 200 } });
      setPoints(r.data?.points || []);
      setTotal(r.data?.total || 0);
    } catch (err) {
      toast.error(errorMessage(err, "Heatmap load failed"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind, days]);

  const max = points.reduce((m, p) => Math.max(m, p.count || 0), 0);

  return (
    <div data-testid="admin-heatmap-tab" className="space-y-4">
      <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-4 flex items-start gap-3">
        <Flame className="w-5 h-5 text-amber-300 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <div className="text-amber-200 font-display text-base">Demand Heatmap</div>
          <div className="text-xs text-white/65 mt-1">
            Most-pinned {kind === "pickup" ? "pickup" : kind === "dropoff" ? "drop-off" : "ride"} spots over the last {days} days.
            Position drivers near the hottest circles for the highest acceptance rate. {total.toLocaleString()} total pings.
          </div>
        </div>
        <button onClick={load} className="text-white/50 hover:text-amber-300 p-1.5">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="w-4 h-4 text-gold/70" />
        {KIND_FILTERS.map((f) => (
          <button
            key={f.key} data-testid={`heatmap-kind-${f.key}`}
            onClick={() => setKind(f.key)}
            className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${
              kind === f.key
                ? "bg-gold/15 text-gold border-gold"
                : "border-white/10 text-white/60 hover:border-gold/40 hover:text-gold"
            }`}
          >{f.label}</button>
        ))}
        <span className="ml-3 text-xs text-white/50">Last:</span>
        {DAY_FILTERS.map((d) => (
          <button
            key={d} data-testid={`heatmap-days-${d}`}
            onClick={() => setDays(d)}
            className={`text-xs px-2.5 py-1.5 rounded-md border transition-colors ${
              days === d
                ? "bg-gold/15 text-gold border-gold"
                : "border-white/10 text-white/60 hover:border-gold/40 hover:text-gold"
            }`}
          >{d}d</button>
        ))}
      </div>

      <MapView height={520} data-testid="heatmap-map">
        {points.map((p, i) => (
          <CircleMarker
            key={`${p.lat}-${p.lng}-${i}`}
            center={[p.lat, p.lng]}
            radius={radiusFor(p.count, max)}
            pathOptions={{
              color: "#f59e0b",
              fillColor: "#fbbf24",
              fillOpacity: 0.45,
              weight: 1,
            }}
          >
            <Tooltip direction="top" offset={[0, -8]}>
              <div className="text-xs">
                <div className="font-semibold">{p.short_label || p.display_name}</div>
                <div>{p.count} pings · {p.country?.toUpperCase() || ""}</div>
              </div>
            </Tooltip>
          </CircleMarker>
        ))}
      </MapView>

      {points.length > 0 && (
        <div className="space-y-1">
          <div className="text-xs text-white/55 uppercase tracking-wider mt-3">Top 10</div>
          {points.slice(0, 10).map((p, i) => (
            <motion.div
              key={`${p.lat}-${p.lng}-${i}`}
              initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-2 text-xs text-white/75 border-b border-white/5 py-1"
            >
              <MapPin className="w-3 h-3 text-amber-300 flex-shrink-0" />
              <div className="flex-1 truncate">{p.short_label || p.display_name}</div>
              <div className="text-amber-300 font-mono">{p.count}×</div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
